import { DataGrid, GridColumns, esES } from '@mui/x-data-grid';
import LinearProgress from '@mui/material/LinearProgress';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import { useCallback, useEffect, useRef, useState } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import { useTranslation } from 'react-i18next';

import { ActionsTable, CustomBasicOptionsModal } from 'components/shared';
import { DeleteSVGIcon } from 'components/shared/Icons';
import { Edge } from 'api/process';

import { Payload, StatusFlowByID } from '../../types';
import { columns } from './columns';

type Props = {
  data: StatusFlowByID[] | Payload[];
  handleRemoveStateFlow: (id: number) => void;
  setDataForEdit: (data: Edge) => void;
  isLoading: boolean;
};

function StateFlowDataGrid({
  data,
  setDataForEdit,
  handleRemoveStateFlow,
  isLoading
}: Props) {
  const [deleteModal, setDeleteModal] = useState<{
    isOpen: boolean;
    id?: number;
    title?: string;
    haveNextStatus?: boolean;
  }>();

  const { t } = useTranslation('global');

  // Ref para evitar eliminar el mismo estado múltiples veces
  const processedIdsRef = useRef<Set<number>>(new Set());

  /**
   * Elimina automáticamente los estados con statusNumberOfChanges === 0
   */
  useEffect(() => {
    if (!data?.length || isLoading) return;

    data.forEach((item) => {
      // Verificar si el estado tiene statusNumberOfChanges === 0 y no es base
      const statusFlowItem = item as StatusFlowByID;
      const statusId = statusFlowItem.statusId || (item as Payload).id;
      const statusNumberOfChanges =
        statusFlowItem.statusNumberOfChanges ??
        statusFlowItem.statusSelected?.length ??
        0;

      if (
        statusId &&
        statusNumberOfChanges === 0 &&
        !item.base &&
        !processedIdsRef.current.has(statusId)
      ) {
        processedIdsRef.current.add(statusId);
        handleRemoveStateFlow(statusId);
      }
    });
  }, [data, isLoading, handleRemoveStateFlow]);

  // Limpiar el ref cuando los datos cambian significativamente
  useEffect(() => {
    const currentIds = new Set(
      data
        ?.map((item) => {
          const statusFlowItem = item as StatusFlowByID;
          return statusFlowItem.statusId || (item as Payload).id;
        })
        .filter((id): id is number => typeof id === 'number') || []
    );

    // Remover IDs que ya no están en los datos
    processedIdsRef.current.forEach((id) => {
      if (!currentIds.has(id)) {
        processedIdsRef.current.delete(id);
      }
    });
  }, [data]);

  const haveNextStatus = useCallback(
    (id: number): boolean =>
      // Verificar si algún estado usa este id como estado final
      // Buscar en statusSelected de cada estado (StatusFlowByID) o en edges (Payload)
      data?.some((item) => {
        const statusFlowItem = item as StatusFlowByID;
        const payloadItem = item as Payload;

        return (
          statusFlowItem.statusSelected?.some(
            (selected) => selected.id === id
          ) ||
          payloadItem.edges?.some((edge: Edge) => edge?.finalStatus?.id === id)
        );
      }) || false,
    [data]
  );

  const closeDeleteModal = useCallback(() => {
    setDeleteModal({ isOpen: false });
  }, []);

  const openModalRemove = useCallback(
    (id: number, title: string) => {
      setDeleteModal({
        id,
        title,
        haveNextStatus: haveNextStatus(id),
        isOpen: true
      });
    },
    [haveNextStatus]
  );

  const extendedColumns: GridColumns<Payload> = [
    ...columns,
    {
      field: 'actions',
      headerName: 'common.commonProperties.actions',
      headerAlign: 'center',
      align: 'center',
      sortable: false,
      renderCell: (cellValues: { row: StatusFlowByID }) => (
        <ActionsTable hideEdit hideRemove>
          <Tooltip placement="top" title={t('common.commonActions.edit')}>
            <IconButton
              disabled={cellValues?.row?.base}
              onClick={() => setDataForEdit(cellValues?.row)}
            >
              <EditIcon
                color={cellValues?.row?.base ? 'disabled' : 'warning'}
              />
            </IconButton>
          </Tooltip>

          <Tooltip placement="top" title={t('common.commonActions.delete')}>
            <IconButton
              disabled={cellValues?.row?.base}
              onClick={() =>
                openModalRemove(
                  cellValues?.row?.statusId,
                  cellValues?.row?.name
                )
              }
            >
              <DeleteSVGIcon
                colorFill={cellValues?.row?.base ? '#808080' : '#E63D3D'}
              />
            </IconButton>
          </Tooltip>
        </ActionsTable>
      ),
      flex: 1,
      minWidth: 130,
      maxWidth: 130
    }
  ];

  return (
    <>
      <CustomBasicOptionsModal
        acceptSubmit={() => {
          handleRemoveStateFlow(deleteModal?.id);
          closeDeleteModal();
        }}
        btnAcceptText={t('common.commonActions.accept')}
        btnCancelText={t('common.commonActions.cancel')}
        cancelSubmit={closeDeleteModal}
        message={
          deleteModal?.haveNextStatus
            ? t('problems.statusFlows.messages.deleteWithDependencies', {
                status: deleteModal?.title
              }) ||
              `¿Está seguro de eliminar ${deleteModal?.title}? Está siendo usado por otros estados.`
            : t('common.commonMessages.deleteConfirmation', {
                title: deleteModal?.title
              })
        }
        show={deleteModal?.isOpen}
      />
      <DataGrid
        columns={extendedColumns.map((column) => ({
          ...column,
          headerName: t(column.headerName)
        }))}
        components={{
          LoadingOverlay: LinearProgress
        }}
        density="comfortable"
        getRowId={(row: StatusFlowByID | Payload) => {
          const statusFlowItem = row as StatusFlowByID;
          const payloadItem = row as Payload;
          return statusFlowItem.statusId || payloadItem.id || Math.random();
        }}
        headerHeight={39}
        loading={isLoading}
        localeText={{
          ...esES.components.MuiDataGrid.defaultProps.localeText,
          noRowsLabel: t('clients.noStatus')
        }}
        rowHeight={52}
        rows={data}
        style={{ height: '100%' }}
        sx={{
          height: '100%',
          '.MuiDataGrid-columnHeaders': {
            backgroundColor: '#0B2E6A',
            color: 'white',
            '.MuiSvgIcon-root': {
              fill: 'white'
            }
          },
          '.css-cxjr35-MuiStack-root': {
            '@media (max-width: 750px)': {
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              width: '100%',
              marginTop: '.5em'
            }
          }
        }}
        disableColumnFilter
        disableColumnMenu
        disableSelectionOnClick
        hideFooter
        keepNonExistentRowsSelected
      />
    </>
  );
}

export default StateFlowDataGrid;
